// Training Center → Prompt Examples tab.
//
// Replaces web/scripts/admin-prompt-examples.js's peNavigate(...)
// two-level browser. Top level: list of configs (per org × datasource
// × stage). Detail level: examples grid + params editor + production
// queries (promote candidates).

import type { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  OrgScope,
  PromptExample,
  PromptExampleConfig,
  PromptExampleConfigDetail,
} from "../../api/training";
import { trainingApi } from "../../api/training";
import { AdminGrid, ErrorBlock, LoadingBlock, formatDateTime } from "../admin/_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function PromptExamplesTab({ bearer, scope }: Props) {
  const [selected, setSelected] = useState<number | null>(null);

  if (selected != null) {
    return (
      <PromptExampleDetail
        bearer={bearer}
        configId={selected}
        onBack={() => setSelected(null)}
      />
    );
  }
  return <PromptExampleConfigList bearer={bearer} scope={scope} onPick={setSelected} />;
}

function PromptExampleConfigList({
  bearer,
  scope,
  onPick,
}: {
  bearer: string;
  scope: OrgScope;
  onPick: (id: number) => void;
}) {
  const [items, setItems] = useState<PromptExampleConfig[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    trainingApi
      .peConfigs(bearer, scope)
      .then((d) => {
        if (cancelled) return;
        setItems(d || []);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  const colDefs = useMemo<ColDef<PromptExampleConfig>[]>(
    () => [
      { field: "organization", headerName: "Organization", flex: 1 },
      {
        field: "datasource",
        headerName: "Datasource",
        flex: 1,
        valueFormatter: (p) => (p.value as string) ?? "—",
      },
      { field: "stage", headerName: "Stage", flex: 1, cellClass: "font-mono text-xs" },
      {
        field: "example_count",
        headerName: "Examples",
        width: 120,
        type: "numericColumn",
      },
    ],
    [],
  );

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;

  if (items.length === 0) {
    return (
      <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
        No prompt example configs found for the selected organization.
      </div>
    );
  }

  return (
    <AdminGrid<PromptExampleConfig>
      rowData={items}
      columnDefs={colDefs}
      rowStyle={{ cursor: "pointer" }}
      onRowClicked={(e) => {
        if (e.data) onPick(e.data.id);
      }}
    />
  );
}

function PromptExampleDetail({
  bearer,
  configId,
  onBack,
}: {
  bearer: string;
  configId: number;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<PromptExampleConfigDetail | null>(null);
  const [examples, setExamples] = useState<PromptExample[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const reload = useCallback(() => {
    let cancelled = false;
    Promise.all([
      trainingApi.peConfigDetail(bearer, configId),
      trainingApi.peExamples(bearer, configId),
    ])
      .then(([d, e]) => {
        if (cancelled) return;
        setDetail(d);
        setExamples(e || []);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, configId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    return reload();
  }, [reload]);

  const handleDelete = async (ex: PromptExample) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this example?")) return;
    setBusyId(ex.id);
    try {
      await trainingApi.peDeleteExample(bearer, configId, ex.id);
      reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock error={error} />;
  if (!detail) return null;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-500 hover:text-neutral-900"
      >
        ← All configs
      </button>

      <div className="flex items-start justify-between gap-4 border-b border-line pb-3">
        <div>
          <div className="text-xs text-neutral-500">
            {detail.organization} · {detail.datasource ?? "(org-level)"}
          </div>
          <h2 className="text-base font-semibold text-neutral-900 mt-0.5 font-mono">
            {detail.stage}
          </h2>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-neutral-900 mb-2">
          Examples ({examples.length})
        </h3>
        {examples.length === 0 ? (
          <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
            No examples yet. Promote a production query to seed the first.
          </div>
        ) : (
          <div className="space-y-3">
            {examples.map((ex) => (
              <div key={ex.id} className="rounded-[14px] border border-line bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[11px] text-neutral-500">
                      {ex.source && (
                        <span className="inline-block rounded-full bg-neutral-100 px-2 py-0.5">
                          {ex.source}
                        </span>
                      )}
                      {ex.always_include && (
                        <span className="inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">
                          always
                        </span>
                      )}
                      {ex.title && <span className="text-neutral-600 truncate">{ex.title}</span>}
                      {ex.created_at && (
                        <span>added {formatDateTime(ex.created_at)}</span>
                      )}
                    </div>
                    <div className="mt-2">
                      <div className="text-[11px] text-neutral-500 uppercase tracking-wide">
                        User query
                      </div>
                      <pre className="mt-1 text-xs whitespace-pre-wrap bg-neutral-50 rounded-md p-2 text-neutral-800">
                        {ex.user_query}
                      </pre>
                    </div>
                    <div className="mt-2">
                      <div className="text-[11px] text-neutral-500 uppercase tracking-wide">
                        Prompt text
                      </div>
                      <pre className="mt-1 text-xs whitespace-pre-wrap bg-neutral-50 rounded-md p-2 text-neutral-800 font-mono max-h-96 overflow-y-auto">
                        {ex.prompt_text}
                      </pre>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(ex)}
                    disabled={busyId === ex.id}
                    className="rounded-md border border-red-300 text-red-700 px-2 h-7 text-xs hover:bg-red-50 shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
